import {
  Box,
  Button,
  Grid,
  Pagination,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi.hook";
import {
  ICreateMeetingRoomReq,
  IMeetingRoom,
  IMeetingSchedule
} from "../../models/meeting";
import MeetingService from "../../services/api/meeting.service";
import { ApproveWrapper } from "./styles";

import MeetingRoomRoundedIcon from "@mui/icons-material/MeetingRoomRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import moment from "moment";
import {
  EMeetingRoomScheduleTypeEnum,
  EMeetingRoomStatus
} from "../../models/common/models.enum";
import CommonHandle from "../../common/handles/common.handles";
import {
  DATE_FORMAT2,
  DATE_REQ_FORMAT,
  TIME_FORMAT
} from "../../config/constants";
import { Avatar, ControlForm, Modal } from "../../components";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import DoDisturbRoundedIcon from "@mui/icons-material/DoDisturbRounded";
import ScheduleCard from "./components/ScheduleCard";
import { toggleMessage } from "../../components/Toast/Toast";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import InputForm from "../../components/controls/InputForm";
import { toggleConfirmModal } from "../../components/ConfirmModal/ConfirmModal";
import AccessTimeFilledRoundedIcon from "@mui/icons-material/AccessTimeFilledRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import DownloadDoneRoundedIcon from "@mui/icons-material/DownloadDoneRounded";
import _ from "lodash";
import { useTranslation } from "react-i18next";

type Props = {};

const Approve = (props: Props) => {
  const { t } = useTranslation();

  const [value, setValue] = React.useState(EMeetingRoomStatus.Pending);
  const [openCreate, setOpenCreate] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const rooms = useApi<IMeetingRoom[]>({ isFetch: true });
  const getPage = useApi<IMeetingSchedule[]>({ isFetch: true, isPaging: true });
  const getAll = useApi<IMeetingSchedule[]>({ isFetch: true });
  const createRoom = useApi<IMeetingRoom>({});
  const deleteSchedule = useApi<IMeetingSchedule>({});
  const deleteRoom = useApi<IMeetingRoom>({});
  const adminUpdateStatus = useApi<IMeetingSchedule>({});

  const validationSchema = Yup.object().shape({
    RoomName: Yup.string().required(
      t("MeetingRoom.Approve.CreateRoomModal.Validation.NameRequired")
    ),
    Location: Yup.string().required(
      t("MeetingRoom.Approve.CreateRoomModal.Validation.LocationRequired")
    ),
    Description: Yup.string().required(
      t("MeetingRoom.Approve.CreateRoomModal.Validation.DescriptionRequired")
    )
  }) as any;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ICreateMeetingRoomReq>({
    resolver: yupResolver(validationSchema)
  });

  function fetchData() {
    getPage.request(
      MeetingService.getPageByStatus({
        Year: moment().year(),
        Month: moment().month() + 1,
        CurrentPage: currentPage,
        PageSize: 10,
        Status: value
      })
    );
  }

  function fetchRooms() {
    rooms.request(
      MeetingService.getRooms({
        CurrentPage: 1,
        PageSize: 10
      })
    );
  }

  useEffect(() => {
    fetchData();
  }, [value, currentPage]);

  function handleAdminUpdateStatus(
    schedule: IMeetingSchedule,
    status: EMeetingRoomStatus
  ) {
    adminUpdateStatus.request(
      MeetingService.adminUpdateStatus({
        id: schedule?.id ?? "",
        status: status
      }).then(res => {
        if (res.success) {
          toggleMessage({
            type: "success",
            message: t("MeetingRoom.Approve.Messages.UpdateSuccess")
          });
          fetchData();
        } else if (!res.success) {
          toggleMessage({
            type: "error",
            message:
              res?.message ?? t("MeetingRoom.Approve.Messages.GenericError")
          });
        }
      })
    );
  }

  useEffect(() => {
    fetchRooms();
    getAll.request(
      MeetingService.getPage({
        Type: EMeetingRoomScheduleTypeEnum.Day,
        Year: moment().year(),
        Month: moment().month() + 1,
        DateTime: moment(moment().format(DATE_FORMAT2)).format(DATE_REQ_FORMAT)
      })
    );
  }, []);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: EMeetingRoomStatus
  ) => {
    setValue(newValue);
    setCurrentPage(1);
  };

  const onSubmit = (request: ICreateMeetingRoomReq) => {
    createRoom.request(
      MeetingService.createRoom({ ...request, IsActive: true }).then(
        response => {
          if (response.success) {
            toggleMessage({
              type: "success",
              message: t("MeetingRoom.Approve.Messages.CreateSuccess")
            });
            fetchRooms();
            setOpenCreate(false);
            reset();
          } else {
            toggleMessage({
              type: "error",
              message:
                response.message ??
                t("MeetingRoom.Approve.Messages.GenericError")
            });
          }
        }
      )
    );
  };

  return (
    <ApproveWrapper>
      <Box width="22%">
        <Typography variant="subtitle2" margin="12px">
          {t("MeetingRoom.Approve.Sidebar.Title")}
        </Typography>
        <Box className="content-box">
          {rooms.data &&
            rooms.data.map((room, i) => (
              <Box key={i}>
                <Tooltip
                  title={t("MeetingRoom.Approve.Sidebar.TooltipDeleteRoom")}
                >
                  <Box
                    display="flex"
                    marginBottom="8px"
                    onMouseUp={() => {
                      toggleConfirmModal({
                        title: t("MeetingRoom.Approve.Confirm.DeleteRoomTitle"),
                        content: t(
                          "MeetingRoom.Approve.Confirm.DeleteRoomContent"
                        ),
                        onSubmit: async () => {
                          deleteRoom.request(
                            MeetingService.deleteRoom(room.id).then(
                              response => {
                                if (response.success) {
                                  toggleMessage({
                                    type: "success",
                                    message: t(
                                      "MeetingRoom.Approve.Messages.RoomDeleted"
                                    )
                                  });
                                  fetchRooms();
                                } else {
                                  toggleMessage({
                                    type: "success",
                                    message:
                                      response?.error ??
                                      t(
                                        "MeetingRoom.Approve.Messages.GenericError"
                                      )
                                  });
                                }
                              }
                            )
                          );
                        },
                        submitText: t(
                          "MeetingRoom.Approve.Confirm.DeleteRoomSubmit"
                        ),
                        type: "warning-red"
                      });
                    }}
                  >
                    <MeetingRoomRoundedIcon />
                    <Box marginLeft="12px">
                      <Typography variant="subtitle2">
                        {room.roomName}
                      </Typography>
                      <Typography variant="caption">
                        {room.location} - {room.description}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              </Box>
            ))}
          <Button
            startIcon={<AddCircleRoundedIcon />}
            onClick={() => setOpenCreate(true)}
          >
            {t("MeetingRoom.Approve.Sidebar.ButtonCreateRoom")}
          </Button>

          <Modal
            title={t("MeetingRoom.Approve.CreateRoomModal.Title")}
            open={openCreate}
            onClose={() => setOpenCreate(false)}
            onSubmit={handleSubmit(onSubmit)}
            textSubmit={t("MeetingRoom.Approve.CreateRoomModal.Submit")}
            loadingState={createRoom.loading}
          >
            <form
              onSubmit={handleSubmit(onSubmit)}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px"
              }}
            >
              <ControlForm
                title={t(
                  "MeetingRoom.Approve.CreateRoomModal.Fields.NameLabel"
                )}
                isRequired
                classname="brand-form"
              >
                <InputForm
                  placeholder={t(
                    "MeetingRoom.Approve.CreateRoomModal.Fields.NamePlaceholder"
                  )}
                  required
                  name="RoomName"
                  errors={errors}
                  control={control}
                  size="small"
                  disabled={createRoom.loading}
                />
              </ControlForm>
              <ControlForm
                title={t(
                  "MeetingRoom.Approve.CreateRoomModal.Fields.DescriptionLabel"
                )}
                isRequired
                classname="brand-form"
              >
                <InputForm
                  placeholder={t(
                    "MeetingRoom.Approve.CreateRoomModal.Fields.DescriptionPlaceholder"
                  )}
                  name="Description"
                  errors={errors}
                  control={control}
                  size="small"
                  disabled={createRoom.loading}
                />
              </ControlForm>
              <ControlForm
                title={t(
                  "MeetingRoom.Approve.CreateRoomModal.Fields.LocationLabel"
                )}
                isRequired
                classname="brand-form"
              >
                <InputForm
                  placeholder={t(
                    "MeetingRoom.Approve.CreateRoomModal.Fields.LocationPlaceholder"
                  )}
                  required
                  name="Location"
                  errors={errors}
                  control={control}
                  size="small"
                  disabled={createRoom.loading}
                />
              </ControlForm>
            </form>
          </Modal>
        </Box>
        <Typography variant="subtitle2" margin="12px">
          {t("MeetingRoom.Approve.TodaySchedule.Title")}
        </Typography>
        {getAll.data
          ?.filter(
            data =>
              CommonHandle.formatDate(data.startDate) ===
              CommonHandle.formatDate(moment().toDate())
          )
          .map(today => (
            <Box key={today.id} marginLeft="12px">
              <ScheduleCard key={today.id} item={today} />
            </Box>
          ))}
      </Box>
      <Box className="content-box" flexGrow={1}>
        <Tabs
          value={value}
          onChange={handleChange}
          textColor="secondary"
          indicatorColor="secondary"
        >
          <Tab
            value={EMeetingRoomStatus.Pending}
            label={t("MeetingRoom.Approve.Tabs.Pending")}
          />
          <Tab
            value={EMeetingRoomStatus.WaitCancel}
            label={t("MeetingRoom.Approve.Tabs.WaitCancel")}
          />
          <Tab
            value={EMeetingRoomStatus.Confirmed}
            label={t("MeetingRoom.Approve.Tabs.Confirmed")}
          />
          <Tab
            value={EMeetingRoomStatus.Completed}
            label={t("MeetingRoom.Approve.Tabs.Completed")}
          />
        </Tabs>
        <Box height="85vh" marginTop="16px">
          {getPage.data &&
            _(getPage.data)
              .groupBy(x => moment(x.startDate).format("MM/yyyy"))
              .map((value, key) => ({ date: key, schedule: value }))
              .value()
              .map(month => (
                <Box key={month.date}>
                  <Typography variant="subtitle2">
                    {t("MeetingRoom.Approve.List.MonthTitle")} {month.date}
                  </Typography>
                  {month.schedule.map(schedule => (
                    <Box
                      key={schedule.id}
                      sx={{
                        border: "1px solid #DEDEDE",
                        display: "flex",
                        borderRadius: "8px",
                        padding: "10px 24px",
                        margin: "14px 0"
                      }}
                    >
                      <Grid container spacing={2} columns={18}>
                        <Grid item xs={2}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "500" }}
                          >
                            {moment(schedule.startDate).format("dddd")}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontSize: "18px" }}
                          >
                            {moment(schedule.startDate).format("DD")}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <div style={{ display: "flex" }}>
                            <AccessTimeFilledRoundedIcon
                              sx={{
                                fontSize: 16,
                                color: "#AAAAB2",
                                marginRight: "4px"
                              }}
                            />
                            <Typography variant="subtitle2">
                              {moment(schedule.startDate).format(TIME_FORMAT)}
                            </Typography>
                          </div>
                          <div>
                            <LocationOnRoundedIcon
                              sx={{
                                fontSize: 16,
                                color: "#AAAAB2",
                                marginRight: "4px"
                              }}
                            />
                            <Typography variant="caption">
                              {schedule.meetingRoom?.roomName ??
                                t("MeetingRoom.Approve.List.NoRoomInformation")}
                            </Typography>
                          </div>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="subtitle2">
                            {schedule?.title}
                          </Typography>
                          <Typography variant="caption">
                            {schedule?.description}
                          </Typography>
                        </Grid>
                        <Grid item xs={5}>
                          {!schedule.meetingRegistrant ? (
                            <p>
                              {t("MeetingRoom.Approve.List.NoRegistrantData")}
                            </p>
                          ) : (
                            <Box display="flex">
                              <Avatar
                                src={schedule.meetingRegistrant?.avatar}
                                width="30px"
                                height="30px"
                              />
                              <Box
                                display="flex"
                                flexDirection="column"
                                marginLeft="8px"
                              >
                                <Typography variant="subtitle2">
                                  {schedule.meetingRegistrant?.fullName}
                                </Typography>
                                <Typography variant="caption">
                                  {schedule.meetingRegistrant?.position}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={3}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            marginLeft="12px"
                          >
                            {value === EMeetingRoomStatus.Pending && (
                              <Button
                                startIcon={<DoDisturbRoundedIcon />}
                                size="small"
                                color="error"
                                onClick={() => {
                                  toggleConfirmModal({
                                    title: t(
                                      "MeetingRoom.Approve.Confirm.RejectTitle"
                                    ),
                                    content: (
                                      <Box>
                                        <p>
                                          {t(
                                            "MeetingRoom.Approve.Confirm.RejectContentLine1"
                                          )}
                                        </p>
                                        <p>
                                          {t(
                                            "MeetingRoom.Approve.Confirm.RejectContentLine2"
                                          )}
                                        </p>
                                      </Box>
                                    ),
                                    onSubmit: async () => {
                                      deleteSchedule.request(
                                        MeetingService.deleteMeeting(
                                          schedule.id
                                        ).then(response => {
                                          if (response.success) {
                                            toggleMessage({
                                              type: "success",
                                              message: t(
                                                "MeetingRoom.Approve.Messages.RegisterDeleted"
                                              )
                                            });
                                            fetchData();
                                          }
                                        })
                                      );
                                    },
                                    submitText: t(
                                      "MeetingRoom.Approve.Confirm.RejectSubmit"
                                    ),
                                    type: "warning-red"
                                  });
                                }}
                              >
                                {t("MeetingRoom.Approve.Buttons.Reject")}
                              </Button>
                            )}
                            {value === EMeetingRoomStatus.Pending && (
                              <Button
                                size="small"
                                sx={{ marginTop: "4px" }}
                                startIcon={<TaskAltRoundedIcon />}
                                onClick={() => {
                                  toggleConfirmModal({
                                    title: t(
                                      "MeetingRoom.Approve.Confirm.ApproveTitle"
                                    ),
                                    content: t(
                                      "MeetingRoom.Approve.Confirm.ApproveContent"
                                    ),
                                    onSubmit: async () =>
                                      handleAdminUpdateStatus(
                                        schedule,
                                        EMeetingRoomStatus.Confirmed
                                      ),
                                    submitText: t(
                                      "MeetingRoom.Approve.Confirm.ApproveSubmit"
                                    ),
                                    type: "confirm"
                                  });
                                }}
                              >
                                {t("MeetingRoom.Approve.Buttons.Approve")}
                              </Button>
                            )}
                            {value === EMeetingRoomStatus.WaitCancel && (
                              <Button
                                size="small"
                                sx={{ marginTop: "4px" }}
                                startIcon={<TaskAltRoundedIcon />}
                                onClick={() => {
                                  toggleConfirmModal({
                                    title: t(
                                      "MeetingRoom.Approve.Confirm.CancelRequestTitle"
                                    ),
                                    content: t(
                                      "MeetingRoom.Approve.Confirm.CancelRequestContent"
                                    ),
                                    onSubmit: async () =>
                                      handleAdminUpdateStatus(
                                        schedule,
                                        EMeetingRoomStatus.Cancel
                                      ),
                                    submitText: t(
                                      "MeetingRoom.Approve.Confirm.CancelRequestSubmit"
                                    ),
                                    type: "infor"
                                  });
                                }}
                              >
                                {t("MeetingRoom.Approve.Buttons.ConfirmCancel")}
                              </Button>
                            )}
                            {value === EMeetingRoomStatus.Confirmed && (
                              <Button
                                size="small"
                                color="info"
                                sx={{ marginTop: "4px" }}
                                startIcon={<DownloadDoneRoundedIcon />}
                                onClick={() => {
                                  toggleConfirmModal({
                                    title: t(
                                      "MeetingRoom.Approve.Confirm.CompleteTitle"
                                    ),
                                    content: t(
                                      "MeetingRoom.Approve.Confirm.CompleteContent"
                                    ),
                                    onSubmit: async () => {
                                      adminUpdateStatus.request(
                                        MeetingService.adminUpdateStatus({
                                          id: schedule?.id ?? "",
                                          status: EMeetingRoomStatus.Completed
                                        }).then(res => {
                                          if (res.success) {
                                            toggleMessage({
                                              type: "success",
                                              message: t(
                                                "MeetingRoom.Approve.Messages.UpdateSuccess"
                                              )
                                            });
                                            fetchData();
                                          } else if (!res.success) {
                                            toggleMessage({
                                              type: "error",
                                              message:
                                                res?.message ??
                                                t(
                                                  "MeetingRoom.Approve.Messages.GenericError"
                                                )
                                            });
                                          }
                                        })
                                      );
                                    },
                                    submitText: t(
                                      "MeetingRoom.Approve.Confirm.CompleteSubmit"
                                    ),
                                    type: "warning-red"
                                  });
                                }}
                              >
                                {t("MeetingRoom.Approve.Buttons.Complete")}
                              </Button>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              ))}

          {getPage.pagging && getPage.pagging.totalPages > 1 && (
            <Box
              sx={{
                margin: "12px 18px",
                padding: "10px",
                display: "flex",
                justifyContent: "end",
                alignItems: "center"
              }}
            >
              <Typography>
                {1 + 10 * (getPage.pagging.currentPage - 1)} -{" "}
                {getPage.pagging.currentPage * 10 < getPage.pagging.totalCount!
                  ? getPage.pagging.currentPage * 10
                  : getPage.pagging.totalCount!}{" "}
                / {getPage.pagging.totalCount}
              </Typography>
              <Pagination
                count={getPage.pagging.totalPages}
                showFirstButton={getPage.pagging.totalPages > 2}
                showLastButton={getPage.pagging.totalPages > 2}
                shape="rounded"
                page={getPage.pagging.currentPage ?? 0}
                onChange={(_, v) => setCurrentPage(v)}
              />
            </Box>
          )}
        </Box>
      </Box>
    </ApproveWrapper>
  );
};

export default Approve;
